import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  Skeleton,
} from '@mui/material';
import ModalMAIQ from 'app/components/ModalMAIQ';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import ConfirmationModal from 'app/components/ConfirmationModal';
import SingleDatePickerMAIQ from 'app/components/SingleDatePickerMAIQ';

interface FooterImageDetailsModalProps {
  open: boolean;
  onClose: () => void;
  data: any;
  onSave: (payload: any) => void;
  onDelete: () => void;
}

const FooterImageDetailsModal: React.FC<FooterImageDetailsModalProps> = ({
  open,
  onClose,
  data,
  onSave,
  onDelete,
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [form, setForm] = React.useState({
    image_description: '',
    image_address: '',
    image_url: '',
    expiry_date: '',
  });

  React.useEffect(() => {
    if (data) {
      setForm({
        image_description: data.image_description || '',
        image_address: data.image_address || '',
        image_url: data.image_url || '',
        expiry_date: data.expiry_date ? data.expiry_date.split('T')[0] : '',
      });
    }
  }, [data]);

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Helper function to check if value should be displayed
  const shouldDisplayValue = (value: any): boolean => {
    if (value === null || value === undefined || value === '') return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (typeof value === 'number' && value === 0) return true; // Allow 0 for numbers
    return true;
  };

  const handleSaveClick = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
    setIsEditing(false);
  };

  if (!data) return null;

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{ width: '600px', maxWidth: '90vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <Box
          sx={{ p: 2, backgroundColor: theme.palette.secondary.main, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h6" fontWeight={600}>
            Footer Image Details
          </Typography>
          <Box>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                sx={{ color: 'white' }}
                onClick={() => setIsEditing(true)}
                disabled={isEditing}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" sx={{ color: 'white', ml: 1 }} onClick={() => setOpenDeleteConfirm(true)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small" sx={{ color: 'white' }} onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, overflowY: 'auto' }}>
          {/* Description */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Image Description
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                value={form.image_description}
                onChange={e => handleChange('image_description', e.target.value)}
                size="small"
              />
            ) : (
              <Typography variant="body2">{data.image_description}</Typography>
            )}
          </Box>

          {/* Image Preview */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Image Preview
            </Typography>
            <Box sx={{ mt: 1 }}>
              <img
                src={isEditing ? form.image_address : data.image_address}
                alt={data.image_description}
                style={{ maxWidth: '100%', borderRadius: 4 }}
              />
            </Box>
          </Box>

          {/* Image Address */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Image Address
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                value={form.image_address}
                onChange={e => handleChange('image_address', e.target.value)}
                size="small"
              />
            ) : (
              <Typography variant="body2">{data.image_address}</Typography>
            )}
          </Box>

          {/* Landing Page URL */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Landing Page URL
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                value={form.image_url}
                onChange={e => handleChange('image_url', e.target.value)}
                size="small"
              />
            ) : (
              <Typography variant="body2">{data.image_url}</Typography>
            )}
          </Box>

          {/* Expiry Date */}
          {shouldDisplayValue(data.expiry_date) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Expiry Date
              </Typography>
              {isEditing ? (
                <SingleDatePickerMAIQ
                  value={form.expiry_date}
                  onChange={(date) => handleChange('expiry_date', date || '')}
                  placeholder="Select expiry date"
                  minDate={new Date()}
                />
              ) : (
                <Typography variant="body2">
                  {new Date(data.expiry_date).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        <Divider />

        {/* Footer action bar */}
        {isEditing && (
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: 'white',
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              zIndex: 9,
              flexShrink: 0,
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setIsEditing(false)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
            {saving ? (
              <Skeleton
                variant="rectangular"
                width={140}
                height={36}
                sx={{
                  borderRadius: 1,
                  bgcolor: 'rgba(15, 40, 76, 0.1)',
                }}
              />
            ) : (
              <Button
                variant="contained"
                onClick={handleSaveClick}
                sx={{ textTransform: 'none', fontWeight: 600, bgcolor: theme.palette.secondary.main, color: 'white' }}
              >
                Save Changes
              </Button>
            )}
          </Box>
        )}
      </Paper>
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        onConfirm={async () => {
          setIsDeleting(true);
          try {
            await onDelete();
            setOpenDeleteConfirm(false);
          } finally {
            setIsDeleting(false);
          }
        }}
        message="Are you sure you want to delete this footer image?"
        loading={isDeleting}
      />
    </ModalMAIQ>
  );
};

export default FooterImageDetailsModal; 